"use client"

import React, { useState, useEffect } from 'react';
import { ChromePicker } from 'react-color';
import delayedAction from './../utils/delayedAction';
import Button from './Button';
import InputAmount from './InputAmount';

import __ from './../utils/translation';

import { ReactComponent as IconTextBold } from './../icons/text-bold.svg';
import { ReactComponent as IconTextItalic } from './../icons/text-italic.svg';
import { ReactComponent as IconTextUnderline } from './../icons/text-underline.svg';
import { ReactComponent as IconTextLineThrough } from './../icons/text-linethrough.svg';
import { ReactComponent as IconTextSub } from './../icons/text-sub.svg';
import { ReactComponent as IconTextSup } from './../icons/text-sup.svg';
import $ from "jquery";
import { fabric } from 'fabric';

import { Card, CardContent } from './ui/card';
import { Button as ShadcnButton } from './ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';
import { Separator } from './ui/separator';
import { ArrowLeft, BoldIcon, Download, ItalicIcon, RegexIcon, UnderlineIcon } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';

const SelectionTextSettings = ({ canvas, activeSelection }) => {

  const [fontFamily, setFontFamily] = useState()
  const [fontSize, setFontSize] = useState(16)
  const [lineHeight, setLineHeight] = useState(20)
  const [charSpacing, setCharSpacing] = useState(0)
  const [textAlign, setTextAlign] = useState('left')
  const [textColor, setTextColor] = useState(false)
  const [colorPicker, setColorPicker] = useState('rgba(0, 0, 0, 1)')
  const [diameter, setDiameter] = useState(1)
  const [kerning, setKerning] = useState(0)
  const [flipped, setFlipped] = useState(false)
  const [content, setContent] = useState()

  const getActiveStyle = (styleName) => {
    if (activeSelection.getSelectionStyles && activeSelection.isEditing) {
      let styles = activeSelection.getSelectionStyles()
      if (styles.find(o => o[styleName] === '')) {
        return ''
      }

      return styles[0][styleName]
    }

    return activeSelection[styleName] || ''
  }

  const setActiveStyle = (styleName, value) => {
    if (activeSelection.setSelectionStyles && activeSelection.isEditing && styleName !== 'fontSize') {
      let style = {}
      style[styleName] = value;
      activeSelection.setSelectionStyles(style)
      activeSelection.setCoords()
    } else {
      activeSelection.set(styleName, value)
    }
    canvas.renderAll()

    if (window.is_iframe)
      window['updateCanvas']();
  }

  const toggleBold = () => {
    setActiveStyle(
      'fontWeight',
      getActiveStyle('fontWeight') === 'bold' ? '' : 'bold'
    )
    canvas.trigger('object:modified')
  }

  const toggleItalic = () => {
    setActiveStyle(
      'fontStyle',
      getActiveStyle('fontStyle') === 'italic' ? '' : 'italic'
    )
    canvas.trigger('object:modified')
  }

  const toggleUnderline = () => {
    setActiveStyle('underline', !getActiveStyle('underline'))
    canvas.trigger('object:modified')
  }

  const toggleLinethrough = () => {
    setActiveStyle('linethrough', !getActiveStyle('linethrough'))
    canvas.trigger('object:modified')
  }

  const toggleSubscript = () => {
    if (getActiveStyle('deltaY') > 0) {
      setActiveStyle('fontSize', undefined)
      setActiveStyle('deltaY', undefined)
    } else {
      activeSelection.setSubscript()
      canvas.renderAll()
    }

    canvas.trigger('object:modified')
  }

  const toggleSuperscript = () => {
    if (getActiveStyle('deltaY') < 0) {
      setActiveStyle('fontSize', undefined)
      setActiveStyle('deltaY', undefined)
    } else {
      activeSelection.setSuperscript()
      canvas.renderAll()
    }

    canvas.trigger('object:modified')
  }

  async function handleFamilyChange(e) {
    setFontFamily(e.target.value);
    setActiveStyle('fontFamily', e.target.value);

    var elem = document.querySelector('#selectFont option[value=' + e.target.value + ']');

    window.a1 = e.target.value;

    fabric.fontPaths[e.target.value] = await fontToBase64('/fonts/' + elem.getAttribute("data-path") +
      e.target.value + '.' + elem.getAttribute("data-extension"));

    window.a2 = fabric.fontPaths[e.target.value];

    canvas.trigger('object:modified')
  }

  async function fontToBase64(fontUrl) {
    const response = await fetch(fontUrl);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  const handleSizeChange = (action, amount) => {
    let size = 0

    if (action === 'decrease') {
      size = fontSize === 0 ? 0 : fontSize - 1
    }

    if (action === 'increase') {
      size = fontSize + 1
    }

    if (action === 'change') {
      size = parseInt(amount)
      if (!Number.isInteger(size)) return
    }

    setFontSize(size)
    setActiveStyle('fontSize', size)
    canvas.trigger('object:modified')
  }

  const handleLineHeightChange = (action, amount) => {
    let heightInPx = 0
    let heightInUnit = 0

    if (action === 'decrease') {
      heightInPx = lineHeight === 0 ? 0 : lineHeight - 1
    }

    if (action === 'increase') {
      heightInPx = lineHeight + 1
    }

    if (action === 'change') {
      heightInPx = parseInt(amount)
      if (!Number.isInteger(heightInPx)) return
    }

    heightInUnit = heightInPx / fontSize

    setLineHeight(heightInPx)
    setActiveStyle('lineHeight', heightInUnit)
    canvas.trigger('object:modified')
  }

  const handleCharSpaceChange = (action, amount) => {
    let spaceInPx = 0
    let spaceInUnitPiece = 0

    if (action === 'decrease') {
      spaceInPx = charSpacing - 1
    }

    if (action === 'increase') {
      spaceInPx = charSpacing + 1
    }

    if (action === 'change') {
      spaceInPx = parseInt(amount)
      if (!Number.isInteger(spaceInPx)) return
    }

    spaceInUnitPiece = spaceInPx / fontSize * 1000

    setCharSpacing(spaceInPx)
    setActiveStyle('charSpacing', spaceInUnitPiece)
    canvas.trigger('object:modified')
  }

  const handleTextAlignChange = (e) => {
    setTextAlign(e.target.value)
    setActiveStyle('textAlign', e.target.value)
    canvas.trigger('object:modified')
  }

  const handleColorChange = (color) => {
    setTextColor(`rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`)
    setActiveStyle('fill', `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`)

    delayedAction(1200, () => {
      canvas.trigger('object:modified')
    })
  }

  const textCurvedDiameter = (action, amount) => {
    let size = 0

    if (action === 'decrease') {
      size = diameter === 0 ? 0 : diameter - 100
    }

    if (action === 'increase') {
      size = diameter + 100
    }

    if (action === 'change') {
      size = parseInt(amount)
      if (!Number.isInteger(size)) return
    }

    setDiameter(size);

    activeSelection.set({
      diameter: diameter * 10,
    });
    canvas.renderAll();
    canvas.trigger('object:modified')
  }

  const textCurvedKerning = (action, amount) => {
    let size = 0

    if (action === 'decrease') {
      size = kerning === 0 ? 0 : kerning - 1
    }

    if (action === 'increase') {
      size = kerning + 1
    }

    if (action === 'change') {
      size = parseInt(amount)
      if (!Number.isInteger(size)) return
    }

    setKerning(size);

    activeSelection.set({
      kerning: kerning * 1000,
    });
    canvas.renderAll();
    canvas.trigger('object:modified')
  }

  const textCurvedFlipped = (event) => {
    setFlipped(event.target.checked);
    activeSelection.set({
      flipped: !flipped,
    });
    canvas.renderAll();
    canvas.trigger('object:modified')
  }

  const textCurvedContent = () => {
    setContent($("#content-curved-text").val())

    var textbox = new fabric.TextCurved(__($("#content-curved-text").val()), {
      left: activeSelection.left,
      top: activeSelection.top,
      width: activeSelection.width,
      fontSize: activeSelection.fontSize,
      fontFamily: activeSelection.fontFamily,
      diameter: activeSelection.diameter,
      kerning: activeSelection.kerning,
      flipped: activeSelection.flipped
    });

    canvas.remove(activeSelection);
    canvas.add(textbox).setActiveObject(textbox)
    textbox.setControlsVisibility({ 'mb': false });

    canvas.renderAll();
    canvas.trigger('object:modified')
  }

  useEffect(() => {
    if (!activeSelection) return

    setFontFamily(activeSelection.fontFamily)
    setFontSize(activeSelection.fontSize)
    setDiameter(activeSelection.diameter)
    setKerning(activeSelection.kerning)
    setFlipped(activeSelection.flipped)
    setContent(activeSelection.text)
    $("#content-curved-text").val(activeSelection.text)

    let lineHeightInPx = Math.floor(fontSize * activeSelection.lineHeight)
    setLineHeight(lineHeightInPx)

    let spaceInPx = Math.round(activeSelection.charSpacing / 1000 * fontSize)
    setCharSpacing(spaceInPx)

    setTextAlign(activeSelection.textAlign)

    setTextColor(activeSelection.fill)

    if (activeSelection.type == "textbox") {
      $(".curved").css("display", "none");
    } else {
      $(".curved").css("display", "inline-block");
    }

  }, [activeSelection, fontSize])

  return (
    <TooltipProvider>
      <Card className="border-0 shadow-none bg-background p-2">
        <CardContent className="p-0 space-y-4">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/50">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  handleClick={() => toggleBold()} 
                  name="bold" 
                  title={__('Negrito')}
                  className="h-9 w-9 rounded-md hover:bg-background transition-colors flex items-center justify-center"
                >
                  <BoldIcon></BoldIcon>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Negrito</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  handleClick={() => toggleItalic()} 
                  name="italic" 
                  title={__('Itálico')}
                  className="h-9 w-9 rounded-md hover:bg-background transition-colors flex items-center justify-center"
                >
                  <ItalicIcon></ItalicIcon>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Itálico</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  handleClick={() => toggleUnderline()} 
                  name="underline" 
                  title={__('Underline')}
                  className="h-9 w-9 rounded-md hover:bg-background transition-colors flex items-center justify-center"
                >
                  <UnderlineIcon></UnderlineIcon>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Sublinhado</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  handleClick={() => toggleLinethrough()} 
                  name="linethrough" 
                  title={__('Linethrough')}
                  className="h-9 w-9 rounded-md hover:bg-background transition-colors flex items-center justify-center"
                >
                  <RegexIcon></RegexIcon>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Riscado</TooltipContent>
            </Tooltip>
          </div>

          <Separator />

          <div id="fonts-btns" className="flex gap-2">
            <ShadcnButton 
              id='fonts-simulador' 
              variant="outline" 
              className="flex-1 h-9"
            >
              Simulador
            </ShadcnButton>
            <ShadcnButton 
              id='fonts-registradas' 
              variant="outline" 
              className="flex-1 h-9"
            >
              Registradas
            </ShadcnButton>
          </div>

          <div id="panel-fonts" className="rounded-lg border bg-card overflow-hidden">
            <div id='fonts-btn' className="p-2 border-b bg-muted/30">
              <ShadcnButton 
                id="btn-fonts" 
                variant="ghost" 
                size="sm"
                className="h-8 w-8 p-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </ShadcnButton>
            </div>
            <div id="list-fonts2" className="p-2"></div>
            <ScrollArea className="h-[300px]">
              <div id="list-fonts" className="grid grid-cols-2 gap-2 p-2">
                <div title='aArigatouGozaimasu' data-path="fonts/" data-extension="ttf" className="font7 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="aArigatouGozaimasu">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='AURORA-Regular' data-path="fonts/" data-extension="ttf" className="font13 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="AURORA-Regular">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='BrazierFlame' data-path="fonts/" data-extension="ttf" className="font16 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="BrazierFlame">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='Catamaran' data-path="fonts/" data-extension="ttf" className="font18 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="Catamaran">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='Coconut House' data-path="fonts/" data-extension="ttf" className="font19 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="Coconut House">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='KawitFree-CndItalic' data-path="fonts/" data-extension="ttf" className="font23 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="KawitFree-CndItalic">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='Lavindir' data-path="fonts/" data-extension="ttf" className="font24 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="Lavindir">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='LTBulletin-Medium' data-path="fonts/" data-extension="ttf" className="font25 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="LTBulletin-Medium">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='MakerVan-Regular' data-path="fonts/" data-extension="ttf" className="font27 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="MakerVan-Regular">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='NCS Rogueland Slab Bold' data-path="fonts/" data-extension="ttf" className="font28 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="NCS Rogueland Slab Bold">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='Number9' data-path="fonts/" data-extension="ttf" className="font29 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="Number9">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='Shock' data-path="fonts/" data-extension="ttf" className="font32 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="Shock">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='Talisman' data-path="fonts/" data-extension="ttf" className="font33 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="Talisman">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='teefont' data-path="fonts/" data-extension="ttf" className="font34 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="teefont">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='BEST-IN-CLASS' data-path="fonts/fontsFutebol/" data-extension="ttf" className="font37 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="BEST-IN-CLASS">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
                <div title='Fut-1' data-path="fonts/fontsFutebol/" data-extension="ttf" className="font39 flex items-center justify-between p-2 rounded-md border hover:bg-accent cursor-pointer transition-colors" value="Fut-1">
                  <span className="text-sm truncate">Font</span>
                  <Download className="h-4 w-4 text-muted-foreground downloadFontSimu" />
                </div>
              </div>
            </ScrollArea>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}

export default SelectionTextSettings
